/**
 * @file apiProfile.js
 * @description API de perfil, avatar, settings, theme, plan y billing
 * Solo UI — endpoints y lógica idénticos al original.
 */

import { getBasePath, API_ENDPOINTS, API_BASE, joinApiOrigin } from '../utils/constants';
import { getToken } from '../services/apiClient';
import { upload as apiClientUpload } from '../services/apiClient';
const basePath = (API_BASE && API_BASE.startsWith('http')) ? '' : (API_BASE || getBasePath());

/**
 * Blindaje contrato avatar: respuesta ya parseada como objeto (apiClient.upload).
 * No altera respuestas de error del backend; solo falla temprano si el éxito viene incompleto.
 * @param {unknown} res
 * @returns {object}
 */
function guardAvatarUploadResponse(res) {
  if (res == null || typeof res !== 'object' || Array.isArray(res)) {
    const e = new Error('avatar.upload.response_not_object');
    e.code = 'AVATAR_UPLOAD_INVALID_SHAPE';
    e.parseError = true;
    throw e;
  }
  const ok =
    res.success === true ||
    res.success === 1 ||
    res.ok === true ||
    res.status === 'ok';
  if (!ok) {
    return res;
  }
  const url = String(res.profile_avatar_url ?? res.avatar_url ?? '').trim();
  if (url) {
    return res;
  }
  const inner = res.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const innerUrl = String(inner.profile_avatar_url ?? inner.avatar_url ?? '').trim();
    if (innerUrl) {
      return {
        ...res,
        success: true,
        profile_avatar_url: inner.profile_avatar_url ?? inner.avatar_url ?? innerUrl,
        avatar_url: inner.avatar_url ?? inner.profile_avatar_url ?? innerUrl,
        profile_avatar_full_url:
          inner.profile_avatar_full_url ?? inner.profile_avatar_url ?? inner.avatar_url ?? innerUrl,
        avatar_version: inner.avatar_version ?? inner.version ?? res.avatar_version ?? res.version ?? null,
        version: inner.version ?? inner.avatar_version ?? res.version ?? res.avatar_version ?? null,
      };
    }
  }
  const e = new Error('avatar.upload.missing_profile_avatar_url');
  e.code = 'AVATAR_UPLOAD_MISSING_URL';
  throw e;
}

export const apiProfileMethods = {
  async getProfile() {
    // Bootstrap initAuth / listeners usan try/catch; el emit global ocurriría antes del catch.
    // _skipForceLogout: evita que 401 en post-login limpie localStorage antes de que AuthContext pueda hacer fallback.
    return this.request(API_ENDPOINTS.PROFILE, { _skipGlobalErrorModal: true, _skipForceLogout: true });
  },

  async getProfileMeta() {
    return this.request(API_ENDPOINTS.PROFILE_META, { _skipGlobalErrorModal: true });
  },

  async postProfileMeta(body) {
    return this.request(API_ENDPOINTS.PROFILE_META, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  },

  async updateProfile(userId, profileData) {
    if (import.meta.env.DEV) {
      const { devLog } = await import('../utils/devConsoleLog');
      devLog('[API][PROFILE] updateProfile called', { userId, tokenPresent: !!getToken() });
    }
    return this.request(API_ENDPOINTS.PROFILE, {
      method: 'POST',
      body: JSON.stringify({
        usuario_id: userId,
        ...profileData
      })
    });
  },

  async setTheme(theme) {
    return this.request(API_ENDPOINTS.USER_THEME, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ theme })
    });
  },

  async uploadPhoto(usuarioId, file, onProgress = null) {
    const uploadUrl = joinApiOrigin(API_ENDPOINTS.UPLOAD_AVATAR);

    const runOnce = () => {
      const formData = new FormData();
      formData.append('usuario_id', usuarioId);
      // Campo canónico: upload_avatar.php prioriza $_FILES['avatar'] luego 'imagen'.
      formData.append('avatar', file);
      return apiClientUpload(uploadUrl, formData, onProgress);
    };

    try {
      const raw = await runOnce();
      return guardAvatarUploadResponse(raw);
    } catch (e) {
      // 413: nginx/PHP cuerpo grande — mensaje específico vía error_key (i18n en capa UI).
      if (e?.status === 413) throw e;
      if (e?.parseError === true) throw e;
      const status = e?.status;
      const msg = e?.message || '';
      const n = Number(status);
      const retriable =
        (status == null || Number.isNaN(n)) ||
        n >= 502 ||
        msg === 'Network error' ||
        msg === 'Timeout';
      if (!retriable) throw e;
      await new Promise((r) => setTimeout(r, 450));
      const rawRetry = await runOnce();
      return guardAvatarUploadResponse(rawRetry);
    }
  },

  async getCountries() {
    return this.request(API_ENDPOINTS.COUNTRIES);
  },

  async completeOnboarding() {
    return this.request(API_ENDPOINTS.USER_ONBOARDING_COMPLETE, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  async getBillingStatus(options = {}) {
    const refresh = options?.refresh ? '?refresh=1' : '';
    const endpoint = API_ENDPOINTS.BILLING_SUBSCRIPTION_STATUS || API_ENDPOINTS.BILLING_STATUS;
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[BILLING_FLOW_OK] calling subscription_status');
    }
    // Lectura de estado para perfil/billing: el backend puede responder success:false (degradación);
    // no lanzar modal global ni dejar rechazo no capturado — la UI maneja suscripción vacía.
    return this.request(`${endpoint}${refresh}`, {
      _skipGlobalErrorModal: true,
    });
  },

  async cancelSubscription() {
    return this.request(API_ENDPOINTS.BILLING_CANCEL_SUBSCRIPTION || API_ENDPOINTS.BILLING_CANCEL, {
      method: 'POST'
    });
  },

  /**
   * Crea sesión de Customer Portal Paddle (URL temporal firmada). Solo backend con PADDLE_API_KEY.
   * @returns {Promise<{ success: boolean, portal_url?: string, code?: string }>}
   */
  async createPaddlePortalSession() {
    try {
      return await this.request(API_ENDPOINTS.BILLING_PADDLE_PORTAL_SESSION, {
        method: 'POST',
        body: JSON.stringify({}),
        _skipGlobalErrorModal: true,
      });
    } catch (e) {
      // request() lanza en HTTP 4xx/5xx aunque venga JSON con code (p. ej. NO_PADDLE_LINK, 403 allowlist).
      const payload = e?.data && typeof e.data === 'object' ? e.data : {};
      return {
        success: false,
        code: payload.code || e?.code || 'PORTAL_SESSION_FAILED',
        error_code: payload.error_code,
        detail: payload.detail,
        message: payload.message || e?.message,
        http_status: e?.status,
      };
    }
  },

  /**
   * @param {'free'|'foundation'} scheduledPlan
   * @returns {Promise<{ success: boolean, scheduled_plan_after_cancel?: string, code?: string }>}
   */
  async setScheduledPlanAfterCancel(scheduledPlan) {
    return this.request(API_ENDPOINTS.BILLING_SCHEDULED_PLAN_CHANGE || '/api/billing/scheduled_plan_change.php', {
      method: 'POST',
      body: JSON.stringify({ scheduled_plan: scheduledPlan }),
      _skipGlobalErrorModal: true,
    });
  },

  async subscribePlan(planCode) {
    return this.request(API_ENDPOINTS.BILLING_SUBSCRIBE || API_ENDPOINTS.BILLING_CREATE_SUBSCRIPTION, {
      method: 'POST',
      body: JSON.stringify({ plan_code: planCode }),
    });
  },

  async changePlan(planCode) {
    return this.request(API_ENDPOINTS.BILLING_CHANGE_PLAN, {
      method: 'POST',
      body: JSON.stringify({ plan_code: planCode }),
    });
  },

  async createBillingPreference(plan = 'pro') {
    return this.request(API_ENDPOINTS.BILLING_CREATE_PREFERENCE, {
      method: 'POST',
      body: JSON.stringify({ plan })
    });
  },

  async createSubscription(plan = 'PRO') {
    return this.subscribePlan(plan);
  },

  async getTenantUsage() {
    return this.request(API_ENDPOINTS.TENANT_USAGE, { _skipDedupe: true });
  },

  async getPlanEffective() {
    return this.request(API_ENDPOINTS.PLAN_EFFECTIVE, { _skipDedupe: true });
  },

  async getPlanSummary() {
    return this.request(API_ENDPOINTS.PLAN_SUMMARY, { _skipDedupe: true });
  },

  async getPlanInfoUnified() {
    return this.request(API_ENDPOINTS.PLAN_INFO);
  },

  async getPlanInfoByPlan(plan) {
    return this.request(`${API_ENDPOINTS.PLAN_INFO}?plan=${encodeURIComponent(plan)}`);
  },

  async getFounderMetrics() {
    const token = getToken();
    if (!token) {
      if (typeof console !== 'undefined') {
        console.warn('[SKIP_FOUNDER_METRICS_NO_AUTH]');
      }
      return { ok: false, skipped_no_auth: true };
    }
    return this.request(API_ENDPOINTS.FOUNDER_METRICS, {
      method: 'GET',
      __source: 'apiProfile.getFounderMetrics',
    });
  },

  async getFounderLeaderboard() {
    return this.request(API_ENDPOINTS.FOUNDER_LEADERBOARD, { method: 'GET' });
  },

  async getMySuggestions(page = 1) {
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    return this.request(`${API_ENDPOINTS.MY_SUGGESTIONS}?page=${safePage}`, { method: 'GET', _skipDedupe: true });
  },

  async createSuggestion(payload = {}) {
    return this.request(API_ENDPOINTS.CREATE_SUGGESTION, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  },

  async getPlansFromTable() {
    return this.request(`${API_ENDPOINTS.PLAN_INFO}?plans_table=true`);
  },

  async getBillingUpgrade() {
    return this.request(API_ENDPOINTS.BILLING_UPGRADE, { method: 'GET' });
  },
};
